import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from './AuthContext';
import { useTheme } from './ThemeContext';
import MobileHeader from './MobileHeader';
import Spinner from './Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPercent,
  faSearch,
  faFilter,
  faDownload,
  faCalendar,
  faDollarSign,
  faChartLine,
  faBuilding,
  faCode,
  faUser,
  faFileExport,
  faEye,
  faSort,
  faSortUp,
  faSortDown,
  faUsers,
  faHandshake,
  faArrowTrendUp,
  faCalculator,
} from '@fortawesome/free-solid-svg-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const AdminAffiliateCommissionsTracking = () => {
  const { currentUser } = useContext(AuthContext);
  const { theme } = useTheme();
  const [commissionsData, setCommissionsData] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAffiliate, setSelectedAffiliate] = useState('ALL');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [sortField, setSortField] = useState('TransactionDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showCharts, setShowCharts] = useState(true);
  const [monthlyData, setMonthlyData] = useState([]);
  const [summaryStats, setSummaryStats] = useState({
    totalCommissions: 0,
    totalProfit: 0,
    totalTransactions: 0,
    uniqueAffiliates: 0,
    averageCommissionPerTransaction: 0,
    averageMarginPerTransaction: 0,
  });

  useEffect(() => {
    if (currentUser?.isAdmin) {
      loadData();
    }
  }, [currentUser]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load affiliates list
      const affiliatesResponse = await fetch(`${process.env.REACT_APP_API_URL}/dataServer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'getAffiliates',
          payload: { userId: currentUser.userId }
        })
      });

      const affiliatesResult = await affiliatesResponse.json();
      console.log('Affiliates API Response:', affiliatesResult);
      if (Array.isArray(affiliatesResult)) {
        setAffiliates(affiliatesResult);
        console.log('Affiliates loaded:', affiliatesResult.length);
      } else if (affiliatesResult && affiliatesResult.success) {
        setAffiliates(affiliatesResult.data || []);
        console.log('Affiliates loaded:', affiliatesResult.data?.length || 0);
      } else {
        console.error('Failed to load affiliates:', affiliatesResult?.error || 'Unexpected response format');
        setAffiliates([]);
      }

      // Load commission data
      await loadCommissionData();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCommissionData = async () => {
    try {
      // This will require creating a new backend endpoint that combines
      // transaction data with affiliate referral information
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dataServer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'getAffiliateCommissionAnalytics',
          payload: { 
            userId: currentUser.userId,
            startDate: startDate,
            endDate: endDate,
            affiliateId: selectedAffiliate !== 'ALL' ? selectedAffiliate : null
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCommissionsData(result.data?.transactions || []);
          setMonthlyData(result.data?.monthlyBreakdown || []);
          setSummaryStats(result.data?.summary || summaryStats);
        }
      }
    } catch (error) {
      console.error('Error loading commission data:', error);
      // For demo purposes, generate mock data
      generateMockData();
    }
  };

  const generateMockData = () => {
    // Mock data for demonstration
    const mockData = [];
    const mockMonthly = [];
    
    // Generate sample transactions for the last 6 months
    for (let i = 0; i < 100; i++) {
      const date = new Date();
      date.setDate(date.getDate() - Math.floor(Math.random() * 180));
      
      const profit = 50 + Math.random() * 200;
      const commissionRate = 0.10 + Math.random() * 0.15; // 10-25%
      const commission = profit * commissionRate;
      
      mockData.push({
        TransactionId: 1000 + i,
        TransactionDate: date.toISOString(),
        AfiliateName: `Affiliate ${Math.floor(Math.random() * 5) + 1}`,
        AffiliateId: Math.floor(Math.random() * 5) + 1,
        CompanyName: `Company ${String.fromCharCode(65 + Math.floor(Math.random() * 26))}`,
        ReferralCode: `CODE${Math.floor(Math.random() * 1000)}`,
        LocationName: 'Sample Location',
        LocationCity: 'Sample City',
        LocationState: 'TX',
        CardNumber: `****${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`,
        Profit: profit,
        CommissionRate: commissionRate,
        CommissionAmount: commission,
        Gallons: 20 + Math.random() * 50,
        UserAmount: profit + 150 + Math.random() * 100,
      });
    }

    // Generate monthly summary
    for (let i = 0; i < 6; i++) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      
      const monthTransactions = mockData.filter(t => {
        const tDate = new Date(t.TransactionDate);
        return tDate.getMonth() === date.getMonth() && tDate.getFullYear() === date.getFullYear();
      });

      mockMonthly.push({
        month: date.toLocaleString('default', { month: 'short', year: 'numeric' }),
        totalTransactions: monthTransactions.length,
        totalCommissions: monthTransactions.reduce((sum, t) => sum + t.CommissionAmount, 0),
        totalProfit: monthTransactions.reduce((sum, t) => sum + t.Profit, 0),
        uniqueAffiliates: [...new Set(monthTransactions.map(t => t.AffiliateId))].length,
      });
    }

    setCommissionsData(mockData);
    setMonthlyData(mockMonthly.reverse());
    
    // Calculate summary stats
    const totalCommissions = mockData.reduce((sum, t) => sum + t.CommissionAmount, 0);
    const totalProfit = mockData.reduce((sum, t) => sum + t.Profit, 0);
    const uniqueAffiliates = [...new Set(mockData.map(t => t.AffiliateId))].length;
    
    setSummaryStats({
      totalCommissions,
      totalProfit,
      totalTransactions: mockData.length,
      uniqueAffiliates,
      averageCommissionPerTransaction: totalCommissions / mockData.length,
      averageMarginPerTransaction: totalProfit / mockData.length,
    });
  };

  const handleFilterSubmit = () => {
    loadCommissionData();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return faSort;
    return sortDirection === 'asc' ? faSortUp : faSortDown;
  };

  const filteredAndSortedData = commissionsData
    .filter(commission => {
      const matchesSearch = searchQuery === '' || 
        commission.AfiliateName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        commission.CompanyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        commission.ReferralCode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        commission.LocationName?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesAffiliate = selectedAffiliate === 'ALL' || 
        commission.AffiliateId?.toString() === selectedAffiliate;
      
      return matchesSearch && matchesAffiliate;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (typeof aVal === 'string') {
        aVal = aVal?.toLowerCase() || '';
        bVal = bVal?.toLowerCase() || '';
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const exportToCSV = () => {
    const headers = [
      'Transaction ID', 'Date', 'Affiliate', 'Company', 'Code', 'Location', 
      'City', 'State', 'Card', 'Profit', 'Commission Rate', 'Commission Amount', 'Gallons'
    ];
    
    const csvData = filteredAndSortedData.map(row => [
      row.TransactionId,
      new Date(row.TransactionDate).toLocaleDateString(),
      row.AfiliateName,
      row.CompanyName,
      row.ReferralCode,
      row.LocationName,
      row.LocationCity,
      row.LocationState,
      row.CardNumber,
      row.Profit?.toFixed(2),
      (row.CommissionRate * 100)?.toFixed(2) + '%',
      row.CommissionAmount?.toFixed(2),
      row.Gallons?.toFixed(2)
    ]);

    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `affiliate-commissions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Chart data
  const monthlyChartData = {
    labels: monthlyData.map(d => d.month),
    datasets: [
      {
        label: 'Total Commissions ($)',
        data: monthlyData.map(d => d.totalCommissions),
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 2,
        yAxisID: 'y',
      },
      {
        label: 'Total Profit ($)',
        data: monthlyData.map(d => d.totalProfit),
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 2,
        yAxisID: 'y',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Monthly Commission & Profit Trends',
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        ticks: {
          callback: function(value) {
            return '$' + value.toLocaleString();
          }
        }
      },
    },
  };

  if (!currentUser?.isAdmin) {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto">
          <FontAwesomeIcon icon={faPercent} className="text-6xl text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600">Admin privileges required to view commission tracking.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner />
        <div className="ml-4">
          <p>Loading commission data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <MobileHeader theme={theme} />
      <div className="mobile-container">
        <div className="p-6 space-y-6">
          
          {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            <FontAwesomeIcon icon={faPercent} className="mr-3" />
            Commission Tracking
          </h1>
          <p className="text-gray-600">Monitor affiliate commissions and profit analysis</p>
        </div>
        <button
          onClick={exportToCSV}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
        >
          <FontAwesomeIcon icon={faFileExport} className="mr-2" />
          Export CSV
        </button>
      </div>

      {/* Summary Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faDollarSign} className="text-green-500 text-xl mr-3" />
            <div>
              <div className="text-lg font-bold text-gray-900">
                ${summaryStats.totalCommissions?.toFixed(2) || '0.00'}
              </div>
              <div className="text-xs text-gray-600">Total Commissions</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faArrowTrendUp} className="text-blue-500 text-xl mr-3" />
            <div>
              <div className="text-lg font-bold text-gray-900">
                ${summaryStats.totalProfit?.toFixed(2) || '0.00'}
              </div>
              <div className="text-xs text-gray-600">Total Profit</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faCalculator} className="text-purple-500 text-xl mr-3" />
            <div>
              <div className="text-lg font-bold text-gray-900">
                {summaryStats.totalTransactions || 0}
              </div>
              <div className="text-xs text-gray-600">Transactions</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faHandshake} className="text-orange-500 text-xl mr-3" />
            <div>
              <div className="text-lg font-bold text-gray-900">
                {summaryStats.uniqueAffiliates || 0}
              </div>
              <div className="text-xs text-gray-600">Affiliates</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faChartLine} className="text-indigo-500 text-xl mr-3" />
            <div>
              <div className="text-lg font-bold text-gray-900">
                ${summaryStats.averageCommissionPerTransaction?.toFixed(2) || '0.00'}
              </div>
              <div className="text-xs text-gray-600">Avg Commission</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faArrowTrendUp} className="text-emerald-500 text-xl mr-3" />
            <div>
              <div className="text-lg font-bold text-gray-900">
                ${summaryStats.averageMarginPerTransaction?.toFixed(2) || '0.00'}
              </div>
              <div className="text-xs text-gray-600">Avg Margin</div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      {showCharts && monthlyData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Monthly Trends</h2>
            <button
              onClick={() => setShowCharts(!showCharts)}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Hide Charts
            </button>
          </div>
          <div className="h-80">
            <Bar data={monthlyChartData} options={chartOptions} />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
              />
              <input
                type="text"
                placeholder="Search affiliates, companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Affiliate</label>
            <select
              value={selectedAffiliate}
              onChange={(e) => setSelectedAffiliate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="ALL">All Affiliates</option>
              {affiliates.map(affiliate => (
                <option key={affiliate.AffiliateId} value={affiliate.AffiliateId}>
                  {affiliate.CompanyName}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="flex items-end">
            <button
              onClick={handleFilterSubmit}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <FontAwesomeIcon icon={faFilter} className="mr-2" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Commission Data Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            Commission Details ({filteredAndSortedData.length})
          </h2>
          {!showCharts && (
            <button
              onClick={() => setShowCharts(true)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Show Charts
            </button>
          )}
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('TransactionDate')}
                >
                  <div className="flex items-center">
                    Date
                    <FontAwesomeIcon icon={getSortIcon('TransactionDate')} className="ml-1" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('AfiliateName')}
                >
                  <div className="flex items-center">
                    Affiliate
                    <FontAwesomeIcon icon={getSortIcon('AfiliateName')} className="ml-1" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('Profit')}
                >
                  <div className="flex items-center">
                    Profit
                    <FontAwesomeIcon icon={getSortIcon('Profit')} className="ml-1" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('CommissionRate')}
                >
                  <div className="flex items-center">
                    Rate
                    <FontAwesomeIcon icon={getSortIcon('CommissionRate')} className="ml-1" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('CommissionAmount')}
                >
                  <div className="flex items-center">
                    Commission
                    <FontAwesomeIcon icon={getSortIcon('CommissionAmount')} className="ml-1" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedData.length > 0 ? filteredAndSortedData.map((commission, index) => (
                <tr key={`${commission.TransactionId}-${index}`} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faCalendar} className="text-gray-400 mr-2" />
                      {new Date(commission.TransactionDate).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faHandshake} className="text-blue-500 mr-2" />
                      <span className="text-sm font-medium text-gray-900">
                        {commission.AfiliateName || 'N/A'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faBuilding} className="text-gray-400 mr-2" />
                      {commission.CompanyName || 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-mono font-semibold rounded-full bg-blue-100 text-blue-800">
                      <FontAwesomeIcon icon={faCode} className="mr-1" />
                      {commission.ReferralCode || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>
                      <div>{commission.LocationName}</div>
                      <div className="text-xs text-gray-500">
                        {commission.LocationCity}, {commission.LocationState}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`font-semibold ${commission.Profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      <FontAwesomeIcon icon={faDollarSign} className="mr-1" />
                      ${(commission.Profit || 0).toFixed(2)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <FontAwesomeIcon icon={faPercent} className="mr-1 text-purple-500" />
                    {((commission.CommissionRate || 0) * 100).toFixed(1)}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                    <FontAwesomeIcon icon={faDollarSign} className="mr-1" />
                    ${(commission.CommissionAmount || 0).toFixed(2)}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    <div className="py-8">
                      <FontAwesomeIcon icon={faPercent} className="text-4xl mb-3 text-gray-300" />
                      <p>No commission data found</p>
                      <p className="text-sm">Adjust your filters or date range to see commission data</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
        </div>
      </div>
    </>
  );
};

export default AdminAffiliateCommissionsTracking;